import type { IntegrationRequestEntry } from "@planerius/contracts/public-api"
import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { useCabinetAccess } from "../../access/use-access"
import { fetchIntegrations, fetchRequestLog } from "../../api/integrations.api"
import { isApiProblem } from "../../api/problem"
import { cx } from "../../lib/cx"
import { countOf } from "../../lib/plural"
import { resolveBranch } from "../../shell/use-branch"
import { ProblemScreen } from "../../shell/states"
import { Button, Chip, Chips, Skeleton } from "../../ui"
import { useResource } from "../directories/use-resource"
import { momentWords, requestLine } from "./developers-model"
import styles from "./developers.module.css"

/**
 * «Журнал обращений» интеграции — /developers/$id/requests (решение владельца 11.10.2026,
 * вопрос 41: показываем). Ответ на «почему у нас не работает касса» — фактом: когда ключ
 * упёрся в лимит, какой запрос не прошёл и почему. Путь — шаблоном, без идентификаторов
 * клиентов. Хранится 30 суток. Листа в макете нет (вопрос дизайнеру 74).
 */
export function RequestsScreen(props: { readonly integrationId: string; readonly onBack: () => void }): ReactNode {
  const { context } = useCabinetAccess()
  const timeZone = resolveBranch(context, null)?.timezone ?? context.tenant.defaultTimezone
  const [only, setOnly] = useState<"all" | "errors">("all")
  const integrations = useResource((signal) => fetchIntegrations(signal), "integrations:requests")
  const log = useResource((signal) => fetchRequestLog(props.integrationId, { only, cursor: null }, signal), `requests:${props.integrationId}:${only}`)
  const [more, setMore] = useState<{ readonly items: readonly IntegrationRequestEntry[]; readonly cursor: string | null }>({
    items: [],
    cursor: null,
  })
  const [loading, setLoading] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    setMore({ items: [], cursor: log.data?.nextCursor ?? null })
  }, [log.data])

  if (log.problem !== null && log.data === null) {
    return <ProblemScreen problem={log.problem} onRetry={log.reload} />
  }
  const name = integrations.data?.items.find((item) => item.id === props.integrationId)?.name ?? "Интеграция"
  const items = [...(log.data?.items ?? []), ...more.items]

  const loadMore = async (): Promise<void> => {
    if (more.cursor === null) return
    setLoading(true)
    setProblem(null)
    try {
      const page = await fetchRequestLog(props.integrationId, { only, cursor: more.cursor })
      setMore((value) => ({ items: [...value.items, ...page.items], cursor: page.nextCursor }))
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.crumbs}>
        <button type="button" className={styles.crumbLink} onClick={props.onBack}>
          Разработчикам
        </button>
        <span className={styles.crumbSep}>/</span>
        <span className={styles.crumbTitle}>{name}</span>
      </div>
      <div className={styles.page}>
        <section className={styles.card} aria-labelledby="requests-title">
          <div>
            <h1 id="requests-title" className={styles.title}>
              Журнал обращений
            </h1>
            <div className={styles.sub}>
              Каждый запрос программы подрядчика к вашим данным. Хранится{" "}
              {countOf(log.data?.retentionDays ?? 30, "сутки", "суток", "суток")}.
            </div>
          </div>
          {log.data === null ? null : (
            <div className={styles.summary} role="status">
              <span>
                За сутки: <b className={styles.strong}>{countOf(log.data.last24h.requests, "обращение", "обращения", "обращений")}</b>
              </span>
              <span>
                неудачных: <b className={log.data.last24h.errors > 0 ? styles.danger : styles.muted}>{String(log.data.last24h.errors)}</b>
              </span>
              <span>
                упёрлось в лимит:{" "}
                <b className={log.data.last24h.rateLimited > 0 ? styles.danger : styles.muted}>{String(log.data.last24h.rateLimited)}</b>
              </span>
            </div>
          )}
          <Chips label="Какие обращения">
            <Chip active={only === "all"} onClick={() => setOnly("all")}>
              Все
            </Chip>
            <Chip active={only === "errors"} onClick={() => setOnly("errors")}>
              Только неудачные
            </Chip>
          </Chips>
          {log.data === null ? (
            <Skeleton width="100%" height={160} />
          ) : items.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.strong}>{only === "errors" ? "Неудачных обращений нет" : "Обращений не было"}</div>
              <div className={styles.sub}>Программа подрядчика ещё не обращалась к данным за последние 30 суток.</div>
            </div>
          ) : (
            <div className={styles.rows}>
              {items.map((entry) => {
                const line = requestLine(entry)
                return (
                  <div key={`${entry.requestId}:${entry.at}`} className={styles.row}>
                    <span className={styles.rowTime}>{momentWords(entry.at, timeZone)}</span>
                    <span className={styles.rowRoute} title={`запрос ${entry.requestId}, ключ ${entry.kid ?? "—"}, адрес ${entry.ip ?? "—"}`}>
                      {line.route}
                    </span>
                    <span className={cx(styles.rowOutcome, line.failed && styles.rowFailed)}>{line.outcome}</span>
                  </div>
                )
              })}
            </div>
          )}
          {problem === null ? null : (
            <div className={styles.problem} role="alert">
              {problem}
            </div>
          )}
          {more.cursor === null ? null : (
            <Button size="sm" variant="secondary" loading={loading} onClick={() => void loadMore()}>
              Показать ещё
            </Button>
          )}
        </section>
      </div>
    </div>
  )
}
